<script lang="ts">
import { ref, computed, onUnmounted, useTemplateRef } from 'vue';

import { parseCssDuration } from '@sg-core/resources/assets/js/utils';

export type PhxsgFocusPointPulse = boolean | 'double';

export interface PhxsgFocusPointPosition {
  x: string;
  y: string;
}

export interface PhxsgFocusPointProps {
  use?: string;
  isActive?: boolean;
  position: PhxsgFocusPointPosition;
  pulse?: PhxsgFocusPointPulse;
  animate?: boolean;
}
</script>

<script setup lang="ts">
/** Default fallback duration in case CSS variable is not available */
const DEFAULT_TRANSITION_DURATION_MS = 200;
const { use = 'div', isActive = false, position, pulse = false, animate = false } = defineProps<PhxsgFocusPointProps>();

const rootRef = useTemplateRef<HTMLElement>('root');

const positionStyles = computed(() => `--phxsg-focus-point-x: ${position.x}; --phxsg-focus-point-y: ${position.y}`);

/**
 * Animation control state.
 *
 * - isHovered/isFocused: Track mouse and keyboard interaction separately
 * - pendingAnimations: Track which animations need to complete their cycle
 * - animationsPaused: True when all animations have completed their cycle
 * - isScalingDown: True during scale-down transition (keeps animations paused)
 */
const isHovered = ref(false);
const isFocused = ref(false);
const pendingAnimations = ref<Set<string>>(new Set());
const animationsPaused = ref(false);
const isScalingDown = ref(false);
const isScalingUp = ref(false);

let scaleDownTimeoutId: ReturnType<typeof setTimeout> | null = null;
let scaleUpFrameId: number | null = null;

/** Clean up pending timers/frames on unmount to prevent memory leaks */
onUnmounted(() => {
  if (scaleDownTimeoutId !== null) {
    clearTimeout(scaleDownTimeoutId);
  }

  if (scaleUpFrameId !== null) {
    cancelAnimationFrame(scaleUpFrameId);
  }
});

/**
 * True when animations should be stopped to allow CSS scale control.
 * This includes active interaction, scale-up transition, and scale-down transition.
 */
const shouldStopAnimations = computed(
  () => isHovered.value || isFocused.value || isScalingDown.value || isScalingUp.value,
);

/**
 * True when the dot should be scaled up (hover scale).
 * Delayed by one frame on scale-up to allow transition from animation to work.
 */
const shouldScale = computed(() => (isHovered.value || isFocused.value) && !isScalingUp.value);

/**
 * Start tracking pulse animations to pause after their current cycle completes.
 * Called when user hovers or focuses on the focus point.
 *
 * Note: We only track pulse ring animations here, not the dot animation.
 * The dot animation is stopped immediately via CSS (--interacting class)
 * so the dot can scale up right away. Pulse rings continue until they
 * complete their cycle, then the --paused class stops them.
 */
function startPauseTracking() {
  // Cancel any pending scale-down transition
  if (scaleDownTimeoutId !== null) {
    clearTimeout(scaleDownTimeoutId);
    scaleDownTimeoutId = null;
  }

  isScalingDown.value = false;

  // Start scale-up: first frame stops animation, second frame applies scale
  // This allows the CSS transition to work properly
  isScalingUp.value = true;

  if (scaleUpFrameId !== null) {
    cancelAnimationFrame(scaleUpFrameId);
  }

  scaleUpFrameId = requestAnimationFrame(() => {
    scaleUpFrameId = requestAnimationFrame(() => {
      isScalingUp.value = false;
      scaleUpFrameId = null;
    });
  });

  // If already paused, do nothing
  if (animationsPaused.value) {
    return;
  }

  pendingAnimations.value.clear();

  if (pulse) {
    pendingAnimations.value.add('phxsg-focus-first-pulse');

    if (pulse === 'double') {
      pendingAnimations.value.add('phxsg-focus-second-pulse');
    }
  }

  // If no pulse animations to track, pause immediately
  if (pendingAnimations.value.size === 0) {
    animationsPaused.value = true;
  }
}

/**
 * Get the scale transition duration from CSS custom property.
 * Falls back to default if element is not available or property is not set.
 *
 * Note: This is intentionally a function (not a computed) because:
 * - getComputedStyle() is expensive and shouldn't be cached eagerly
 * - CSS variables can change dynamically (e.g., reduced motion preferences)
 * - We only need the value at the moment of use (in resumeAnimations)
 */
function getScaleTransitionDuration() {
  if (!rootRef.value) {
    return DEFAULT_TRANSITION_DURATION_MS;
  }

  const duration = getComputedStyle(rootRef.value).getPropertyValue('--sys-motion-duration-short-2');

  return parseCssDuration(duration, DEFAULT_TRANSITION_DURATION_MS);
}

/**
 * Resume animations from their paused state.
 * Called when user stops hovering and focus leaves the focus point.
 *
 * This starts a scale-down transition period where animations remain paused
 * to allow the CSS transition to complete smoothly.
 */
function resumeAnimations() {
  pendingAnimations.value.clear();

  // Start scale-down transition - keep animations paused during this time
  isScalingDown.value = true;

  const transitionDuration = getScaleTransitionDuration();

  scaleDownTimeoutId = setTimeout(() => {
    isScalingDown.value = false;
    animationsPaused.value = false;
    scaleDownTimeoutId = null;
  }, transitionDuration);
}

/**
 * Handle animation iteration events.
 * When an animation completes a cycle, remove it from the pending set.
 * When all pending animations have completed, pause them.
 */
function handleAnimationIteration(event: AnimationEvent) {
  if (pendingAnimations.value.size === 0) {
    return;
  }

  pendingAnimations.value.delete(event.animationName);

  if (pendingAnimations.value.size === 0) {
    animationsPaused.value = true;
  }
}

function handleMouseEnter() {
  isHovered.value = true;
  startPauseTracking();
}

function handleMouseLeave() {
  isHovered.value = false;

  if (!isFocused.value) {
    resumeAnimations();
  }
}

function handleFocusIn() {
  isFocused.value = true;
  startPauseTracking();
}

function handleFocusOut() {
  isFocused.value = false;

  if (!isHovered.value) {
    resumeAnimations();
  }
}

const rootClasses = computed(() => [
  'phxsg-focus__point',
  {
    'phxsg-focus__point--active': isActive,
    'phxsg-focus__point--animate': animate,
    'phxsg-focus__point--interacting': shouldStopAnimations.value,
    'phxsg-focus__point--scaled': shouldScale.value,
    'phxsg-focus__point--paused': animationsPaused.value,
  },
]);

const pulseClasses = computed(() => [
  'phxsg-focus__pulse',
  {
    'phxsg-focus__pulse--double': pulse === 'double',
  },
]);

/**
 * Event handlers for animation control.
 * Only bound when pulse or animate is enabled, avoiding unnecessary event listeners.
 */
const animationEventHandlers = computed(() => {
  if (!pulse && !animate) {
    return {};
  }

  return {
    mouseenter: handleMouseEnter,
    mouseleave: handleMouseLeave,
    focusin: handleFocusIn,
    focusout: handleFocusOut,
    animationiteration: handleAnimationIteration,
  };
});
</script>

<template>
  <component :is="use" ref="root" :class="rootClasses" :style="positionStyles" v-on="animationEventHandlers">
    <div v-if="pulse" :class="pulseClasses" @animationiteration="handleAnimationIteration" />
    <slot />
  </component>
</template>
